import * as z from 'zod/mini'
import {
  duplicateOriginIndexes,
  EditEntityBody,
  endAfterStart,
  EntityPost,
  entityFields,
  entityPostFields,
  getDateTimeInTimezone,
  getUTCintoTimezone,
  keyedIssue,
  ManagedEntity,
  openingDay,
  PutEntityPostBody,
  refineWithKey,
  WeekDay,
} from '@seiri/shared/common'

const addressText = z.string().check(z.trim(), z.maxLength(255))

/**
 * The edit-premise page is four independent tabs, and each one saves on its own. The models below
 * are one per tab, so an invalid field on one tab can never block saving another - the shared model
 * that used to back the whole page meant a bad legacy phone number disabled every button on it.
 *
 * The toggles on the first tab are deliberately absent: they save the moment they are flipped, so
 * they are component state, not form state. The field rules are the API's (`entityFields`,
 * `editEntityBody`); the phone also keeps the characters the page has always allowed.
 */
export const premiseInfoFormModel = z.object({
  name: entityFields.name,
  email: entityFields.email,
  shortDescription: entityFields.shortDescription,
  description: entityFields.description,
  phone: entityFields.phone.check(
    refineWithKey('validation.phoneCharacters', (value: string) => /^[0-9+/ -]*$/.test(value)),
  ),
  addressLine1: addressText,
  zip: addressText,
  city: addressText,
  state: addressText,
})
export type PremiseInfoFormModel = z.infer<typeof premiseInfoFormModel>

/** The country the page defaults to when the premise has never had one stored. */
const DEFAULT_STATE = 'Slovensko'

type PremiseInfoSource = Pick<
  ManagedEntity,
  'name' | 'email' | 'shortDescription' | 'description' | 'phone' | 'address'
>

export const toPremiseInfoFormModel = (entity: PremiseInfoSource): PremiseInfoFormModel => ({
  name: entity.name,
  email: entity.email,
  shortDescription: entity.shortDescription,
  description: entity.description,
  phone: entity.phone,
  addressLine1: entity.address.addressLine1,
  zip: entity.address.zip,
  city: entity.address.city,
  state: entity.address.state || DEFAULT_STATE,
})

/** Tab 1's inputs → `PATCH manage/entities/:entityId`. The toggles are sent separately. */
export const toEditEntityInfoBody = (model: PremiseInfoFormModel): EditEntityBody => ({ ...model })

/** Tab 4: the company data behind the premise, stored on its payment settings and address. */
export const premiseCompanyFormModel = z.object({
  companyName: addressText,
  companyId: addressText,
  companyVatId: addressText,
  companyIBAN: addressText,
})
export type PremiseCompanyFormModel = z.infer<typeof premiseCompanyFormModel>

export const toPremiseCompanyFormModel = (entity: Pick<ManagedEntity, 'paymentSettings'>): PremiseCompanyFormModel => ({
  companyName: entity.paymentSettings?.companyName || '',
  companyId: entity.paymentSettings?.companyId || '',
  companyVatId: entity.paymentSettings?.vatId || '',
  companyIBAN: entity.paymentSettings?.iban || '',
})

/** Tab 4 → `PATCH manage/entities/:entityId`. The body's own names differ from the form's. */
export const toEditEntityCompanyBody = (model: PremiseCompanyFormModel): EditEntityBody => ({
  companyName: model.companyName,
  companyId: model.companyId,
  vatId: model.companyVatId,
  iban: model.companyIBAN,
})

/**
 * Tab 2: the publish switch and the whole week, every day present (a closed day is an empty list).
 * Each day is the API's `openingDay`, so an interval that ends before it starts or overlaps another
 * is shown under its own field rather than refused by the request.
 */
export const premiseHoursFormModel = z.object({
  enabled: z.boolean(),
  week: z.object({
    [WeekDay.Monday]: openingDay,
    [WeekDay.Tuesday]: openingDay,
    [WeekDay.Wednesday]: openingDay,
    [WeekDay.Thursday]: openingDay,
    [WeekDay.Friday]: openingDay,
    [WeekDay.Saturday]: openingDay,
    [WeekDay.Sunday]: openingDay,
  }),
})
export type PremiseHoursFormModel = z.infer<typeof premiseHoursFormModel>

const WEEK = [
  WeekDay.Monday,
  WeekDay.Tuesday,
  WeekDay.Wednesday,
  WeekDay.Thursday,
  WeekDay.Friday,
  WeekDay.Saturday,
  WeekDay.Sunday,
] as const

/** A day missing from the stored week is closed. Only `start`/`end` are kept - the API reads nothing else. */
export const toPremiseHoursFormModel = (
  entity: Pick<ManagedEntity, 'openingHours' | 'openingHoursEnabled'>,
): PremiseHoursFormModel => {
  const day = (weekDay: WeekDay) =>
    (entity.openingHours?.[weekDay] ?? []).map((hour) => ({ start: hour.start, end: hour.end }))
  return {
    enabled: entity.openingHoursEnabled,
    week: Object.fromEntries(WEEK.map((weekDay) => [weekDay, day(weekDay)])) as PremiseHoursFormModel['week'],
  }
}

/**
 * Tab 2 → `PATCH manage/entities/:entityId`. `enabled` is the premise's publish switch: turning it
 * off hides the week from the public pages and leaves the hours stored, so switching it back on
 * restores what was there. That is why the hours are sent even when the switch is off.
 */
export const toEditEntityHoursBody = (model: PremiseHoursFormModel): EditEntityBody => ({
  openingHours: model.week,
  openingHoursEnabled: model.enabled,
})

/**
 * Tab 5: the websites allowed to embed the premise's public elements, one editable row each - the
 * list is the form, so an address typed into a row is always part of what is saved. Each row is the
 * API's URL rule; on top of it a row on a site an earlier row already lists is an error, because the
 * API would silently collapse the two into one (`uniqueOrigins`).
 */
export const premiseOriginsFormModel = z.object({
  origins: entityFields.allowedOrigins.check(
    z.superRefine((origins, ctx) => {
      for (const index of duplicateOriginIndexes(origins)) {
        ctx.addIssue(keyedIssue('validation.duplicateOrigin', [index]))
      }
    }),
  ),
})
export type PremiseOriginsFormModel = z.infer<typeof premiseOriginsFormModel>

export const toPremiseOriginsFormModel = (entity: Pick<ManagedEntity, 'allowedOrigins'>): PremiseOriginsFormModel => ({
  origins: [...entity.allowedOrigins],
})

/** Tab 5 → `PATCH manage/entities/:entityId`. Sends the whole list; the API replaces it and normalises. */
export const toEditEntityOriginsBody = (model: PremiseOriginsFormModel): EditEntityBody => ({
  allowedOrigins: model.origins,
})

/**
 * Tab "Upozornenie na stránke". The bounds are browser-local wall-clock `Date`s, as `seiri-date-time-field` picks
 * them, read as the premise's time; `null` is unbounded. The rules are the API's (`entityPostFields`).
 */
export const premiseAnnouncementFormModel = z
  .object({
    title: entityPostFields.title,
    content: entityPostFields.content,
    showFrom: z.nullable(z.date()),
    hideAfter: z.nullable(z.date()),
  })
  .check(z.superRefine(endAfterStart('showFrom', 'hideAfter')))
export type PremiseAnnouncementFormModel = z.infer<typeof premiseAnnouncementFormModel>

/** The stored announcement (UTC instants) → the form, in the premise's `timezone`. */
export const toPremiseAnnouncementFormModel = (
  post: EntityPost | null,
  timezone: string,
): PremiseAnnouncementFormModel => ({
  title: post?.title ?? '',
  content: post?.content ?? '',
  showFrom: post?.showFrom ? getUTCintoTimezone(post.showFrom, timezone) : null,
  hideAfter: post?.hideAfter ? getUTCintoTimezone(post.hideAfter, timezone) : null,
})

/** The form → `PUT manage/entities/:entityId/post`: the whole announcement, the bounds as UTC instants. */
export const toPutEntityPostBody = (model: PremiseAnnouncementFormModel, timezone: string): PutEntityPostBody => ({
  title: model.title.trim(),
  content: model.content,
  showFrom: model.showFrom ? getDateTimeInTimezone(model.showFrom, timezone).toISOString() : null,
  hideAfter: model.hideAfter ? getDateTimeInTimezone(model.hideAfter, timezone).toISOString() : null,
})
